import {z} from "zod/v4-mini"
import {registerTemplateGraphNode} from "#template/template-node/register"
import {IsNonNull} from "@cm/utils"
import {GeometryAttributeNode, pointGeometryAttributeLikeMarker} from "#template/nodes/geometry-graph/attribute/attribute-node"
import {createPointGeometryAttributeData} from "#template/nodes/geometry-graph/interfaces"
import type {GeometryAttributeData, GeometryValueData, GeometryValueRefData} from "#template/nodes/geometry-graph/interfaces"
import type {ScalarArg} from "#template/nodes/geometry-graph/attribute/scalar-arg"
import {scalarArg} from "#template/nodes/geometry-graph/attribute/scalar-arg"
import type {TemplateNodeDefinition} from "#template/template-node/types"
import {getTemplateNodeReturns} from "#template/template-node/validation"
import type {BuilderInlet} from "#template/runtime-graph/graph-builder"
import type {GraphBuilderScope} from "#template/runtime-graph/graph-builder-scope"
import type {NodeEvaluator} from "#template/node-evaluator"
import type {PointGeometryAttributeLikeNode} from "#template/nodes/geometry-graph/attribute/attribute-node"
import type {NonNullableParameters} from "#template/nodes/geometry-graph/common/geometry-graph-node"
import type {GeomBuilderContext, ValueRef} from "#template/geometry-processing/geometry-graph"
import {Primitives} from "#template/geometry-processing/geometry-graph"
import {applyGeometryOperator} from "#template/nodes/geometry-graph/common/geometry-graph-node-declaration"

export type PointGeometryPointSet1Parameters = {
    data: (ScalarArg | null)[]
}

const definition: TemplateNodeDefinition<PointGeometryPointSet1Parameters> = {
    nodeClass: "PointGeometryPointSet1",
    validation: {
        parameters: z.object({
            data: z.array(z.nullable(scalarArg)),
        }),
        returns: getTemplateNodeReturns(),
    },
}

function emptyListToNull<T>(list: T[]): T[] | null {
    return list.length > 0 ? list : null
}
const resolveScalarArg = (value: GeometryValueData | number): GeometryValueRefData | number => (typeof value === "number" ? value : value.ref)

@registerTemplateGraphNode
export class PointGeometryPointSet1 extends GeometryAttributeNode<0, PointGeometryPointSet1Parameters> implements PointGeometryAttributeLikeNode {
    get [pointGeometryAttributeLikeMarker]() {
        return true as const
    }

    public override get definition() {
        return definition
    }

    protected override hasValidParameters(
        parameters: PointGeometryPointSet1Parameters,
    ): parameters is PointGeometryPointSet1Parameters & NonNullableParameters<PointGeometryPointSet1Parameters> {
        return parameters.data.length > 0
    }

    protected override setupGeometryData(
        parameters: NonNullableParameters<PointGeometryPointSet1Parameters>,
        scope: GraphBuilderScope,
        evaluator: NodeEvaluator,
        ctx: GeomBuilderContext,
    ): {evaluatedGeometryData: BuilderInlet<GeometryAttributeData<0> | null>; invalidator?: BuilderInlet<null>} {
        const {data} = parameters

        const [evaluatedDataValid, evaluatedDataInvalid] = scope.branch(
            scope.pureLambda(
                scope.list(data.filter(IsNonNull).map((x) => evaluator.evaluate(x))),
                (data) => emptyListToNull(data.filter(IsNonNull)),
                "evaluatedInputData",
            ),
        )

        const geometryAttributeData = scope.pureLambda(
            scope.tuple(evaluatedDataValid, ctx),
            ([data, ctx]) =>
                createPointGeometryAttributeData(
                    applyGeometryOperator((...args: (ValueRef | number)[]) => Primitives.pointSet1(ctx, args), ...data.map(resolveScalarArg)),
                ),
            "createPrimitive",
        )

        return {evaluatedGeometryData: geometryAttributeData, invalidator: evaluatedDataInvalid}
    }
}
